import { useCallback, useContext, useEffect } from 'react';
import { ConditionType } from '@/types/cml-api';
import { WindowWithDataLayer } from '@/features/analytics/types';
import { CookieConsentContext } from '@/components/cookie-consent/cookie-consent-context';
import useModal from '@/utils/use-modal';
import PopupView from './_components/popup-view';
import { ModalClosedAtString } from './_utils/constants';
import { convertToMilliseconds } from './_utils/convert-to-milliseconds';
import { useGetPopup } from './_utils/use-get-popup';
import { PopupAction, sendPopUpEvent } from '../analytics/datalayer';
import { INTERACTIFY_PARAM_KEY } from '../interactify/constants';
export interface Props {
  cat_id?: string;
  tag_id?: string;
  brand_id?: string;
  isHomePage?: boolean;
}
declare const window: WindowWithDataLayer;
export default function Popup({
  isHomePage,
  cat_id,
  tag_id,
  brand_id
}: Props) {
  const {
    data
  } = useGetPopup({
    isHomePage,
    cat_id,
    tag_id,
    brand_id
  });
  const {
    addCallbackToQueue
  } = useContext(CookieConsentContext);
  const {
    handleCloseModal,
    handleOpenModal,
    isModalOpen
  } = useModal(false);
  const setDataLayer = useCallback((action: PopupAction) => {
    sendPopUpEvent({
      elementId: data?.id || null,
      action,
      page_path: window.location.href
    });
  }, [data]);
  const handleClose = () => {
    handleCloseModal();
    const now = Date.now();
    localStorage.setItem(`${ModalClosedAtString}${data?.id}`, now.toString());
  };
  const handleLinkButtonClick = () => {
    setDataLayer(PopupAction.Click);
    handleClose();
  };
  const openPopupHandler = useCallback(() => addCallbackToQueue(handleOpenModal), [addCallbackToQueue, handleOpenModal]);
  const checkIsVideoOpen = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    return params.has(INTERACTIFY_PARAM_KEY);
  }, []);
  useEffect(() => {
    if (!data) return;
    const trigger = isHomePage ? data.homePageTrigger : data.plpTrigger;
    const postponeInMs = convertToMilliseconds(data.postPone?.conditionValue || 0, data.postPone?.type || ConditionType.Second);
    const now = new Date();
    const lastClosed = localStorage.getItem(`${ModalClosedAtString}${data.id}`);
    const lastClosedAt = lastClosed ? parseInt(lastClosed) : null;
    if (lastClosedAt && now.getTime() - lastClosedAt < postponeInMs) {
      return;
    }
    let timer: NodeJS.Timeout;
    if (trigger?.type === ConditionType.Hour || trigger?.type === ConditionType.Minute || trigger?.type === ConditionType.Second) {
      const triggerInMs = convertToMilliseconds(trigger?.conditionValue || 0, trigger?.type as ConditionType.Second | ConditionType.Minute | ConditionType.Hour || ConditionType.Second);
      timer = setTimeout(() => {
        const isVideoOpen = checkIsVideoOpen();
        if (isVideoOpen) return;
        openPopupHandler();
        setDataLayer(PopupAction.View);
      }, triggerInMs);
    }
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollPercent = scrollPosition / documentHeight * 100;
      const isVideoOpen = checkIsVideoOpen();
      if (isVideoOpen) return;
      if (trigger?.conditionValue && scrollPercent >= trigger.conditionValue) {
        openPopupHandler();
        setDataLayer(PopupAction.View);
        window.removeEventListener('scroll', handleScroll);
      }
    };
    if (trigger?.type === ConditionType.Percent) {
      window.addEventListener('scroll', handleScroll);
    }
    return () => {
      window.removeEventListener('scroll', handleScroll);
      clearTimeout(timer);
    };
  }, [data, openPopupHandler, isHomePage, setDataLayer, checkIsVideoOpen]);
  if (!data) return;
  return <PopupView data={data} isOpen={isModalOpen} handleClose={handleClose} handleCopyButtonClick={() => setDataLayer(PopupAction.Copy)} handleLinkButtonClick={handleLinkButtonClick} data-sentry-element="PopupView" data-sentry-component="Popup" data-sentry-source-file="index.tsx" />;
}